import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, vModelSelect as _vModelSelect, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "assign-intervenant-modal" }
const _hoisted_2 = { class: "mode-selection" }
const _hoisted_3 = {
  key: 0,
  class: "existing-section"
}
const _hoisted_4 = { class: "search-header" }
const _hoisted_5 = { class: "search-box" }
const _hoisted_6 = ["placeholder"]
const _hoisted_7 = {
  key: 0,
  class: "selection-counter"
}
const _hoisted_8 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_9 = {
  key: 1,
  class: "intervenant-list"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "selection-checkbox" }
const _hoisted_12 = ["checked", "onClick"]
const _hoisted_13 = { class: "intervenant-info" }
const _hoisted_14 = { class: "intervenant-name" }
const _hoisted_15 = { class: "intervenant-details" }
const _hoisted_16 = { class: "type-badge" }
const _hoisted_17 = {
  key: 0,
  class: "detail-text"
}
const _hoisted_18 = {
  key: 0,
  class: "checkmark"
}
const _hoisted_19 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_20 = { class: "modal-actions" }
const _hoisted_21 = ["disabled"]
const _hoisted_22 = {
  key: 0,
  class: "spinner"
}
const _hoisted_23 = {
  key: 1,
  class: "new-section"
}
const _hoisted_24 = { class: "form-group" }
const _hoisted_25 = {
  for: "intervenantName",
  class: "required"
}
const _hoisted_26 = ["placeholder"]
const _hoisted_27 = {
  key: 0,
  class: "error-message"
}
const _hoisted_28 = { class: "form-group" }
const _hoisted_29 = {
  for: "intervenantType",
  class: "required"
}
const _hoisted_30 = {
  value: "",
  disabled: ""
}
const _hoisted_31 = { value: "ARCHITECT" }
const _hoisted_32 = { value: "CONTRACTOR" }
const _hoisted_33 = { value: "CONSULTANT" }
const _hoisted_34 = { value: "SUPPLIER" }
const _hoisted_35 = { value: "SUBCONTRACTOR" }
const _hoisted_36 = { value: "OTHER" }
const _hoisted_37 = {
  key: 0,
  class: "error-message"
}
const _hoisted_38 = { class: "form-group" }
const _hoisted_39 = { for: "intervenantEmail" }
const _hoisted_40 = ["placeholder"]
const _hoisted_41 = {
  key: 0,
  class: "error-message"
}
const _hoisted_42 = { class: "form-group" }
const _hoisted_43 = { for: "intervenantPhone" }
const _hoisted_44 = ["placeholder"]
const _hoisted_45 = {
  key: 0,
  class: "error-message"
}
const _hoisted_46 = { class: "form-group" }
const _hoisted_47 = { for: "intervenantAddress" }
const _hoisted_48 = ["placeholder"]
const _hoisted_49 = { class: "modal-actions" }
const _hoisted_50 = ["disabled"]
const _hoisted_51 = {
  key: 0,
  class: "spinner"
}

import { ref as vueRef, computed as vueComputed, onMounted, watch as vueWatch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import Modal from '@/components/base/Modal.vue'
import intervenantsService from '@/services/intervenants.service'
import firmIntervenantsService from '@/services/firmIntervenants.service'
import { getAvatarColor, getInitials } from '@/utils/uiHelpers'
import type { IntervenantData } from '@/types/services'
import type { FirmIntervenantResponse } from '@/services/firmIntervenants.service'

// Aliases to avoid TypeScript errors
interface Phase {
  id?: number
  projectId: number
  name: string
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'
}

interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: string
  email: string
  phone?: string
  address?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AssignIntervenantModal',
  props: {
    show: { type: Boolean },
    phase: {},
    projectId: {},
    existingIntervenants: { default: () => [] }
  },
  emits: ["close", "intervenant-assigned"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const ref = vueRef
const computed = vueComputed
const watch = vueWatch

const props = __props

const emit = __emit

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// State
const mode = ref<'existing' | 'new'>('existing')
const selectedIntervenantIds = ref<number[]>([])
const searchQuery = ref('')
const loadingFirmIntervenants = ref(false)
const firmIntervenants = ref<FirmIntervenantResponse[]>([])
const submitting = ref(false)

// Form state for new intervenant
const form = ref<IntervenantData>({
  name: '',
  type: 'OTHER',
  email: '',
  phone: '',
  address: ''
})

const errors = ref<Record<string, string>>({})

// Email validation regex
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Phone validation regex (international format)
const phoneRegex = /^[\d\s\-\+\(\)]+$/

// Computed - Get assigned intervenant IDs
const assignedIntervenantIds = computed(() => {
  if (!props.existingIntervenants || !Array.isArray(props.existingIntervenants)) {
    return []
  }
  return props.existingIntervenants.map((i: Intervenant) => i.id).filter((id: number | undefined): id is number => id !== undefined)
})

// Computed - Filter out already assigned intervenants
const availableFirmIntervenants = computed(() => {
  return firmIntervenants.value.filter(
    (intervenant: FirmIntervenantResponse) => !assignedIntervenantIds.value.includes(intervenant.id)
  )
})

// Computed - Filter by search query
const filteredFirmIntervenants = computed(() => {
  if (!searchQuery.value) {
    return availableFirmIntervenants.value
  }
  
  const query = searchQuery.value.toLowerCase()
  return availableFirmIntervenants.value.filter((intervenant: FirmIntervenantResponse) =>
    intervenant.name.toLowerCase().includes(query) ||
    (intervenant.email && intervenant.email.toLowerCase().includes(query)) ||
    (intervenant.phone && intervenant.phone.includes(query))
  )
})

// Computed - Button text based on selection
const assignButtonText = computed(() => {
  const count = selectedIntervenantIds.value.length
  if (count === 0) {
    return t('workspace.modal.assign')
  } else if (count === 1) {
    return t('workspace.modal.assign')
  } else {
    return t('workspace.modal.assignMultiple', { count })
  }
})

// Methods
const isSelected = (id: number): boolean => {
  return selectedIntervenantIds.value.includes(id)
}

const toggleSelection = (id: number) => {
  const index = selectedIntervenantIds.value.indexOf(id)
  if (index > -1) {
    selectedIntervenantIds.value.splice(index, 1)
  } else {
    selectedIntervenantIds.value.push(id)
  }
}

const clearSelection = () => {
  selectedIntervenantIds.value = []
}
const loadFirmIntervenants = async () => {
  loadingFirmIntervenants.value = true
  try {
    const response = await firmIntervenantsService.getFirmIntervenants()
    if (response) {
      firmIntervenants.value = response
    }
  } catch (error) {
    console.error('Error loading firm intervenants:', error)
    notifyError(t('workspace.errors.failedToLoadFirmIntervenants'))
  } finally {
    loadingFirmIntervenants.value = false
  }
}

const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.name || form.value.name.trim().length < 2) {
    errors.value.name = t('workspace.modal.errors.nameRequired')
    return false
  }
  
  if (!form.value.type) {
    errors.value.type = t('workspace.modal.errors.typeRequired')
    return false
  }
  
  if (form.value.email && !emailRegex.test(form.value.email)) {
    errors.value.email = t('workspace.modal.errors.invalidEmail')
    return false
  }
  
  if (form.value.phone && !phoneRegex.test(form.value.phone)) {
    errors.value.phone = t('workspace.modal.errors.invalidPhone')
    return false
  }
  
  return true
}

const handleAssignExisting = async () => {
  if (selectedIntervenantIds.value.length === 0) return
  
  submitting.value = true
  try {
    // Assign all selected intervenants
    const selectedIntervenants = firmIntervenants.value.filter((i: FirmIntervenantResponse) => 
      selectedIntervenantIds.value.includes(i.id)
    )
    
    if (selectedIntervenants.length === 0) {
      throw new Error('Selected intervenants not found')
    }

    // Create all intervenants in parallel
    const assignmentPromises = selectedIntervenants.map((intervenant: FirmIntervenantResponse) =>
      intervenantsService.createIntervenant(props.projectId.toString(), {
        name: intervenant.name,
        type: intervenant.type as any,
        email: intervenant.email || '',
        phone: intervenant.phone || '',
        address: intervenant.address || ''
      })
    )
    
    await Promise.all(assignmentPromises)
    
    const count = selectedIntervenants.length
    if (count === 1) {
      notifySuccess(t('workspace.success.intervenantAssigned'))
    } else {
      notifySuccess(t('workspace.success.intervenantsAssigned', { count }))
    }
    
    emit('intervenant-assigned')
    closeModal()
  } catch (error) {
    console.error('Error assigning intervenants:', error)
    notifyError(t('workspace.errors.intervenantAssignFailed'))
  } finally {
    submitting.value = false
  }
}

const handleCreateNew = async () => {
  if (!validateForm()) return
  
  submitting.value = true
  try {
    await intervenantsService.createIntervenant(props.projectId.toString(), form.value)
    notifySuccess(t('workspace.success.intervenantCreated'))
    emit('intervenant-assigned')
    closeModal()
  } catch (error) {
    console.error('Error creating intervenant:', error)
    notifyError(t('workspace.error.intervenantSaveFailed'))
  } finally {
    submitting.value = false
  }
}

const closeModal = () => {
  mode.value = 'existing'
  selectedIntervenantIds.value = []
  searchQuery.value = ''
  form.value = {
    name: '',
    type: 'OTHER',
    email: '',
    phone: '',
    address: ''
  }
  errors.value = {}
  emit('close')
}

// Watch for modal open
watch(() => props.show, (newValue: boolean) => {
  if (newValue && mode.value === 'existing' && firmIntervenants.value.length === 0) {
    loadFirmIntervenants()
  }
})

// Load on mount if modal is already open
onMounted(() => {
  if (props.show && mode.value === 'existing') {
    loadFirmIntervenants()
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    show: _ctx.show,
    title: _ctx.$t('workspace.modal.assignIntervenant'),
    size: "large",
    "close-on-backdrop-click": !_unref(submitting),
    loading: _unref(submitting),
    onClose: closeModal
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("button", {
            class: _normalizeClass(["mode-button", { active: _unref(mode) === 'existing' }]),
            onClick: _cache[0] || (_cache[0] = ($event: any) => (mode.value = 'existing'))
          }, [
            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "icon" }, "👥", -1)),
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('workspace.modal.assignExisting')), 1)
          ], 2),
          _createElementVNode("button", {
            class: _normalizeClass(["mode-button", { active: _unref(mode) === 'new' }]),
            onClick: _cache[1] || (_cache[1] = ($event: any) => (mode.value = 'new'))
          }, [
            _cache[9] || (_cache[9] = _createElementVNode("span", { class: "icon" }, "➕", -1)),
            _createElementVNode("span", null, _toDisplayString(_ctx.$t('workspace.modal.createNew')), 1)
          ], 2)
        ]),
        (_unref(mode) === 'existing')
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _cache[10] || (_cache[10] = _createElementVNode("span", { class: "search-icon" }, "🔍", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(searchQuery) ? (searchQuery).value = $event : null)),
                    type: "text",
                    placeholder: _ctx.$t('workspace.modal.searchIntervenants'),
                    class: "search-input"
                  }, null, 8, _hoisted_6), [
                    [_vModelText, _unref(searchQuery)]
                  ])
                ]),
                (_unref(selectedIntervenantIds).length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createTextVNode(_toDisplayString(_ctx.$t('workspace.modal.selectedCount', { count: _unref(selectedIntervenantIds).length })) + " ", 1),
                      _createElementVNode("button", {
                        class: "clear-selection-btn",
                        onClick: clearSelection
                      }, _toDisplayString(_ctx.$t('common.clear')), 1)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              (_unref(loadingFirmIntervenants))
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "spinner" }, null, -1)),
                    _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')), 1)
                  ]))
                : (_unref(filteredFirmIntervenants).length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(filteredFirmIntervenants), (intervenant) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: intervenant.id,
                          class: _normalizeClass(["intervenant-item", { selected: isSelected(intervenant.id) }]),
                          onClick: ($event: any) => (toggleSelection(intervenant.id))
                        }, [
                          _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("input", {
                              type: "checkbox",
                              checked: isSelected(intervenant.id),
                              onClick: _withModifiers(($event: any) => (toggleSelection(intervenant.id)), ["stop"])
                            }, null, 8, _hoisted_12)
                          ]),
                          _createElementVNode("div", {
                            class: "intervenant-avatar",
                            style: _normalizeStyle({ backgroundColor: _unref(getAvatarColor)(intervenant.name) })
                          }, _toDisplayString(_unref(getInitials)(intervenant.name)), 5),
                          _createElementVNode("div", _hoisted_13, [
                            _createElementVNode("div", _hoisted_14, _toDisplayString(intervenant.name), 1),
                            _createElementVNode("div", _hoisted_15, [
                              _createElementVNode("span", _hoisted_16, _toDisplayString(_ctx.$t(`intervenants.types.${intervenant.type}`)), 1),
                              (intervenant.email)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(intervenant.email), 1))
                                : _createCommentVNode("", true)
                            ])
                          ]),
                          (isSelected(intervenant.id))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_18, "✓"))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_10))
                      }), 128))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_19, [
                      _cache[12] || (_cache[12] = _createElementVNode("span", { class: "empty-icon" }, "👥", -1)),
                      _createElementVNode("p", null, _toDisplayString(_ctx.$t('workspace.modal.noFirmIntervenants')), 1)
                    ])),
              _createElementVNode("div", _hoisted_20, [
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-secondary",
                  onClick: closeModal
                }, _toDisplayString(_ctx.$t('common.cancel')), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "btn btn-primary",
                  disabled: _unref(selectedIntervenantIds).length === 0 || _unref(submitting),
                  onClick: handleAssignExisting
                }, [
                  (_unref(submitting))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_22))
                    : _createCommentVNode("", true),
                  _createTextVNode(" " + _toDisplayString(_unref(submitting) ? _ctx.$t('common.assigning') : _unref(assignButtonText)), 1)
                ], 8, _hoisted_21)
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_23, [
              _createElementVNode("form", {
                onSubmit: _withModifiers(handleCreateNew, ["prevent"]),
                class: "intervenant-form"
              }, [
                _createElementVNode("div", _hoisted_24, [
                  _createElementVNode("label", _hoisted_25, _toDisplayString(_ctx.$t('workspace.modal.intervenantName')), 1),
                  _withDirectives(_createElementVNode("input", {
                    id: "intervenantName",
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(form).name) = $event)),
                    type: "text",
                    placeholder: _ctx.$t('workspace.modal.intervenantNamePlaceholder'),
                    required: "",
                    minlength: "2",
                    maxlength: "100"
                  }, null, 8, _hoisted_26), [
                    [_vModelText, _unref(form).name]
                  ]),
                  (_unref(errors).name)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_27, _toDisplayString(_unref(errors).name), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_28, [
                  _createElementVNode("label", _hoisted_29, _toDisplayString(_ctx.$t('workspace.modal.intervenantType')), 1),
                  _withDirectives(_createElementVNode("select", {
                    id: "intervenantType",
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(form).type) = $event)),
                    required: ""
                  }, [
                    _createElementVNode("option", _hoisted_30, _toDisplayString(_ctx.$t('workspace.modal.selectType')), 1),
                    _createElementVNode("option", _hoisted_31, _toDisplayString(_ctx.$t('intervenants.types.ARCHITECT')), 1),
                    _createElementVNode("option", _hoisted_32, _toDisplayString(_ctx.$t('intervenants.types.CONTRACTOR')), 1),
                    _createElementVNode("option", _hoisted_33, _toDisplayString(_ctx.$t('intervenants.types.CONSULTANT')), 1),
                    _createElementVNode("option", _hoisted_34, _toDisplayString(_ctx.$t('intervenants.types.SUPPLIER')), 1),
                    _createElementVNode("option", _hoisted_35, _toDisplayString(_ctx.$t('intervenants.types.SUBCONTRACTOR')), 1),
                    _createElementVNode("option", _hoisted_36, _toDisplayString(_ctx.$t('intervenants.types.OTHER')), 1)
                  ], 512), [
                    [_vModelSelect, _unref(form).type]
                  ]),
                  (_unref(errors).type)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_37, _toDisplayString(_unref(errors).type), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_38, [
                  _createElementVNode("label", _hoisted_39, _toDisplayString(_ctx.$t('workspace.modal.email')), 1),
                  _withDirectives(_createElementVNode("input", {
                    id: "intervenantEmail",
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(form).email) = $event)),
                    type: "email",
                    placeholder: _ctx.$t('workspace.modal.emailPlaceholder'),
                    maxlength: "100"
                  }, null, 8, _hoisted_40), [
                    [_vModelText, _unref(form).email]
                  ]),
                  (_unref(errors).email)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_41, _toDisplayString(_unref(errors).email), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_42, [
                  _createElementVNode("label", _hoisted_43, _toDisplayString(_ctx.$t('workspace.modal.phone')), 1),
                  _withDirectives(_createElementVNode("input", {
                    id: "intervenantPhone",
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(form).phone) = $event)),
                    type: "tel",
                    placeholder: _ctx.$t('workspace.modal.phonePlaceholder'),
                    maxlength: "20"
                  }, null, 8, _hoisted_44), [
                    [_vModelText, _unref(form).phone]
                  ]),
                  (_unref(errors).phone)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_45, _toDisplayString(_unref(errors).phone), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_46, [
                  _createElementVNode("label", _hoisted_47, _toDisplayString(_ctx.$t('workspace.modal.address')), 1),
                  _withDirectives(_createElementVNode("textarea", {
                    id: "intervenantAddress",
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(form).address) = $event)),
                    placeholder: _ctx.$t('workspace.modal.addressPlaceholder'),
                    maxlength: "200",
                    rows: "3"
                  }, null, 8, _hoisted_48), [
                    [_vModelText, _unref(form).address]
                  ])
                ]),
                _createElementVNode("div", _hoisted_49, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn btn-secondary",
                    onClick: closeModal
                  }, _toDisplayString(_ctx.$t('common.cancel')), 1),
                  _createElementVNode("button", {
                    type: "submit",
                    class: "btn btn-primary",
                    disabled: _unref(submitting)
                  }, [
                    (_unref(submitting))
                      ? (_openBlock(), _createElementBlock("span", _hoisted_51))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(_unref(submitting) ? _ctx.$t('common.creating') : _ctx.$t('common.create')), 1)
                  ], 8, _hoisted_50)
                ])
              ], 32)
            ]))
      ])
    ]),
    _: 1
  }, 8, ["show", "title", "close-on-backdrop-click", "loading"]))
}
}

})